import { ExpandCollapse, Select, Table, TableHeaderRowWithExpander, ToolMenu, ToolMenuContent } from '@aon/aon-tws-common-ui';
import React, { useState } from 'react';
import styled from 'styled-components';
import { ISurveyDimension, ISurveyQuestionEditModel } from '../../../models';
import { ColumnHelper } from '../../../utilities';
import { getUniqueDimensionKey } from '../../../utilities/DimensionHelper';
import { useManageSurveyDimensionColumns } from '../../../utilities/hooks/useManageSurveyDimensionsColumns';
import ManageSurveyDimensionsTableRow from './ManageSurveyDimensionsTableRow';

const StyledExpandCollapse = styled(ExpandCollapse)`
	margin-bottom: 50px;
`;

export interface IManageSurveyDimensionsTableProps {
	dimensions: ISurveyDimension[];
	selectedQuestionsMap: { [key: string]: ISurveyQuestionEditModel[] };
	onToggleSingleQuestionSelection: (dimension: ISurveyDimension, question: ISurveyQuestionEditModel, isSelected: boolean) => void;
	onToggleAllQuestionsSelection: (dimension: ISurveyDimension, questions: ISurveyQuestionEditModel[], isSelected: boolean) => void;
	onEdit: (dimension: ISurveyDimension) => void;
	onMoveQuestions: (sourceDimensions: ISurveyDimension, questionsToMove: ISurveyQuestionEditModel[]) => void;
	onCopyQuestions: (sourceDimensions: ISurveyDimension, questionsToMove: ISurveyQuestionEditModel[]) => void;
	onRemoveQuestions: (sourceDimension: ISurveyDimension, questionsToRemove: ISurveyQuestionEditModel[]) => void;
	onDelete: (dimension: ISurveyDimension) => void;
}

const ManageSurveyDimensionsTable = ({
	dimensions,
	selectedQuestionsMap,
	onToggleSingleQuestionSelection,
	onToggleAllQuestionsSelection,
	onEdit,
	onMoveQuestions,
	onCopyQuestions,
	onRemoveQuestions,
	onDelete,
}: IManageSurveyDimensionsTableProps) => {
	const deriveRowExpansionStateFromDimensions = (isExpanded: boolean) => {
		return dimensions.reduce(
			(expandedDictionary, dimension) => ({ ...expandedDictionary, [getUniqueDimensionKey(dimension)]: isExpanded }),
			{},
		);
	};

	const { manageSurveyDimensionsColumns } = useManageSurveyDimensionColumns();
	const [availableColumns, setAvailableColumns] = useState(manageSurveyDimensionsColumns);
	const [allRowsExpanded, setAllRowsExpanded] = useState(false);
	const [rowExpansionStateMap, setRowExpansionStateMap] = useState(deriveRowExpansionStateFromDimensions(false));

	const handleColumnSelectionApplied = (values: string[]) => {
		const updatedColumns = ColumnHelper.UpdateSelectedColumns(availableColumns, values);
		setAvailableColumns(updatedColumns);
	};

	const handleRowExpansionToggle = (dimension: ISurveyDimension, isExpanded: boolean) => {
		const updatedRowExpansionStateMap = {
			...rowExpansionStateMap,
			[getUniqueDimensionKey(dimension)]: isExpanded,
		};
		setRowExpansionStateMap(updatedRowExpansionStateMap);
	};

	return (
		<div className='col-12'>
			<StyledExpandCollapse
				isExpanded={allRowsExpanded}
				onExpandCollapse={(expanded) => {
					setAllRowsExpanded(expanded);
					setRowExpansionStateMap(deriveRowExpansionStateFromDimensions(expanded));
				}}
			/>
			<ToolMenu>
				<ToolMenuContent type={'left'}>
					<Select
						size='lg'
						selectSize='lg'
						dropdownSize='lg'
						items={availableColumns}
						displayText='Column'
						multiple={true}
						showCount={true}
						onApply={(values?: string | string[] | undefined) => handleColumnSelectionApplied(values as string[])}
						selectedValues={ColumnHelper.GetSelectedColumnNames(availableColumns)}
					/>
				</ToolMenuContent>
			</ToolMenu>
			<Table responsive={true} lastalign='right'>
				<thead>
					<TableHeaderRowWithExpander>
						{ColumnHelper.GetColumnHeaders(availableColumns).map((header) => (
							<th key={header.label}>{header.label}</th>
						))}
					</TableHeaderRowWithExpander>
				</thead>
				<tbody>
					{dimensions.map((dimension) => (
						<ManageSurveyDimensionsTableRow
							key={getUniqueDimensionKey(dimension)}
							dimension={dimension}
							isExpanded={rowExpansionStateMap[getUniqueDimensionKey(dimension)]}
							onToggleIsExpanded={handleRowExpansionToggle}
							selectedColumns={availableColumns}
							onEdit={onEdit}
							onMoveQuestions={onMoveQuestions}
							onCopyQuestions={onCopyQuestions}
							onRemoveQuestions={onRemoveQuestions}
							onDelete={onDelete}
							selectedQuestions={selectedQuestionsMap[getUniqueDimensionKey(dimension)]}
							onToggleSingleQuestionSelection={onToggleSingleQuestionSelection}
							onToggleAllQuestionsSelection={onToggleAllQuestionsSelection}
						/>
					))}
				</tbody>
			</Table>
		</div>
	);
};

export default ManageSurveyDimensionsTable;
